import React from "react";
import LegalLayout from "./LegalLayout";
import useSeo from "@/hooks/useSeo";

export default function Privacy() {
  useSeo({
    title: "Privacy Policy",
    description: "Informativa sul trattamento dei dati personali ai sensi del GDPR (Reg. UE 2016/679). Titolare: DASABO SRL, Palermo. Nessun tracker di terze parti, nessuna profilazione.",
    path: "/privacy",
  });
  return (
    <LegalLayout
      testid="legal-privacy"
      kicker="Legal"
      title="Privacy Policy"
      updatedAt="27 febbraio 2026"
    >
      <p>
        La presente informativa è resa ai sensi degli artt. 13 e 14 del Regolamento UE 2016/679
        (&laquo;<strong>GDPR</strong>&raquo;) e del D.Lgs. 196/2003 come modificato dal D.Lgs. 101/2018
        (&laquo;<strong>Codice Privacy</strong>&raquo;).
      </p>

      <h2>1. Titolare del trattamento</h2>
      <p>
        <strong>DASABO SRL</strong>, Via Imperatore Federico 100, 90143 Palermo (PA), Italia,
        P.IVA/CF 07407380828, PEC <strong>dasabo@pec.it</strong>. Per ogni richiesta relativa al
        trattamento dei dati puoi scriverci a <strong>privacy@dasabo.com</strong>.
      </p>

      <h2>2. Tipologie di dati trattati</h2>
      <ul>
        <li><strong>Dati di registrazione</strong>: nome, cognome, email, password (hash bcrypt),
        eventuale codice referral;</li>
        <li><strong>Dati fiscali dell&apos;utente</strong>: P.IVA, codice fiscale, denominazione,
        indirizzo, PEC, codice destinatario SDI, regime fiscale;</li>
        <li><strong>Contenuti caricati</strong>: file PDF di fornitori esteri, contenuto delle
        email inoltrate al proprio indirizzo di inbound, allegati;</li>
        <li><strong>Dati estratti dall&apos;AI</strong>: fornitore, importo, valuta, servizio,
        VAT del fornitore, natura del costo;</li>
        <li><strong>Log tecnici</strong>: indirizzo IP, user-agent, timestamp accessi, chiamate API;</li>
        <li><strong>Metadati SDI</strong>: UUID di trasmissione, notifiche di consegna/scarto,
        ricevute XML firmate dal Sistema di Interscambio;</li>
        <li><strong>Integrazioni</strong>: token OAuth di Fatture in Cloud (cifrati at rest con
        Fernet), configurazioni OpenAPI.it, chiavi Resend.</li>
      </ul>

      <h2>3. Finalità e base giuridica</h2>
      <ul>
        <li><strong>Erogazione del servizio</strong> (art. 6 §1 b GDPR — esecuzione contrattuale):
        creazione account, generazione XML, trasmissione allo SDI;</li>
        <li><strong>Adempimenti fiscali</strong> (art. 6 §1 c GDPR — obbligo legale): conservazione
        dei documenti fiscali dell&apos;utente per il periodo previsto dalla normativa tributaria;</li>
        <li><strong>Miglioramento del servizio</strong> (art. 6 §1 f GDPR — legittimo interesse):
        analisi aggregate anonime, prevenzione frodi, sicurezza infrastruttura;</li>
        <li><strong>Marketing diretto</strong> (art. 6 §1 a — consenso): invio newsletter, novità,
        offerte commerciali, previa esplicita opt-in dell&apos;utente e con possibilità di revoca
        immediata.</li>
      </ul>

      <h2>4. Modalità del trattamento</h2>
      <p>
        Il trattamento avviene con strumenti informatici, presso i data center dei nostri fornitori
        cloud, applicando misure di sicurezza adeguate: cifratura at-rest e in-transit, hash
        password con bcrypt, JWT con secret rotabile, backup criptati, accessi tracciati, principio
        del minimo privilegio.
      </p>

      <h2>5. Destinatari e responsabili esterni</h2>
      <p>Per erogare il servizio ci avvaliamo dei seguenti fornitori, tutti nominati responsabili
      esterni del trattamento ex art. 28 GDPR:</p>
      <ul>
        <li><strong>MongoDB Atlas</strong> (database) — hosting UE;</li>
        <li><strong>Dasabo Cloud</strong> (compute) — hosting UE;</li>
        <li><strong>Resend</strong> (email transazionali e inbound) — hosting EU-West-1;</li>
        <li><strong>Anthropic / OpenAI / Google</strong> (modelli AI per estrazione PDF) — trasferimento
        extra-UE con clausole contrattuali standard;</li>
        <li><strong>TeamSystem S.p.A.</strong> (Fatture in Cloud) — hosting UE;</li>
        <li><strong>OpenAPI.it</strong> (trasmissione SDI) — hosting UE.</li>
      </ul>

      <h2>6. Trasferimenti extra-UE</h2>
      <p>
        I modelli AI di terze parti operano parzialmente in territorio extra-UE (USA). Il
        trasferimento avviene ai sensi degli artt. 46-49 GDPR mediante <strong>Standard Contractual
        Clauses</strong> (SCC) approvate dalla Commissione Europea. I PDF vengono inviati al modello
        AI in forma cifrata (HTTPS) e non sono utilizzati per addestrare i modelli.
      </p>

      <h2>7. Periodo di conservazione</h2>
      <ul>
        <li>Dati dell&apos;account: fino a chiusura dell&apos;account + 30 giorni di grace period;</li>
        <li>Fatture e XML generati: 10 anni (obbligo di conservazione fiscale ex DPR 600/1973 e
        DPR 633/1972);</li>
        <li>Log tecnici: 12 mesi salvo obblighi di legge diversi;</li>
        <li>Comunicazioni PEC: fino a chiusura account + 6 mesi.</li>
      </ul>

      <h2>8. Diritti dell&apos;interessato</h2>
      <p>
        In qualsiasi momento puoi esercitare i diritti di cui agli artt. 15-22 GDPR: accesso,
        rettifica, cancellazione, limitazione, opposizione, portabilità, revoca del consenso.
        Puoi contattarci a <strong>privacy@dasabo.com</strong> o via PEC a
        <strong> dasabo@pec.it</strong>. Se ritieni che il trattamento violi il GDPR, puoi presentare
        reclamo al <strong>Garante per la protezione dei dati personali</strong> (garanteprivacy.it).
      </p>

      <h2>9. Cookie</h2>
      <p>
        Il sito utilizza esclusivamente cookie tecnici essenziali. Per il dettaglio consulta la nostra
        <a href="/cookies" className="text-[#1D4ED8] hover:underline"> Cookie Policy</a>.
      </p>

      <h2>10. Modifiche</h2>
      <p>
        Ci riserviamo di aggiornare la presente informativa. In caso di modifiche sostanziali ne
        daremo comunicazione via email agli utenti registrati.
      </p>
    </LegalLayout>
  );
}
